import React, { useEffect, useRef, useState, ReactNode } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { FaGithub, FaTwitter, FaNpm, FaCode, FaTerminal, FaCheckCircle, FaLayerGroup, FaRocket } from 'react-icons/fa';
import logo from "./logo.png";
import demo from "./demo.png";

interface Feature {
  icon: JSX.Element;
  title: string;
  description: string;
}

const features: Feature[] = [
  {
    icon: <FaCode className="text-accent text-4xl mb-4" />,
    title: 'Next.js & Go',
    description: 'Build your frontend with Next.js and smart contracts with Go.',
  },
  {
    icon: <FaLayerGroup className="text-accent text-4xl mb-4" />,
    title: 'Pre-installed Configuration',
    description: 'Includes TypeScript, Tailwind CSS, shadcn-ui, and more.',
  },
  {
    icon: <FaCheckCircle className="text-accent text-4xl mb-4" />,
    title: 'Kalp DLT Chain',
    description: 'Develop, deploy, test, and verify smart contracts on Kalp DLT.',
  },
  {
    icon: <FaTerminal className="text-accent text-4xl mb-4" />,
    title: 'Example dApps',
    description: 'Multiple full-stack dApps to help you get started quickly.',
  },
  {
    icon: <FaCode className="text-accent text-4xl mb-4" />,
    title: 'Kalp Studio Integration',
    description: 'Seamless integration with Kalp Studio for easy deployment.',
  },
  {
    icon: <FaTerminal className="text-accent text-4xl mb-4" />,
    title: 'Command Line Interface',
    description: 'A powerful CLI to help you build and ship fast on Kalp DLT.',
  },
];

interface SectionProps {
  children: ReactNode;
  id: string;
}

const Section: React.FC<SectionProps> = ({ children, id }) => {
  const controls = useAnimation();
  const ref = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          controls.start('visible');
        } else {
          setIsVisible(false);
          controls.start('hidden');
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [controls]);

  return (
    <motion.section
      ref={ref}
      id={id}
      className="min-h-screen flex items-center justify-center py-20"
      initial="hidden"
      animate={controls}
      variants={{
        visible: { opacity: 1, y: 0 },
        hidden: { opacity: 0, y: isVisible ? -50 : 50 },
      }}
      transition={{ duration: 0.5 }}
    >
      {children}
    </motion.section>
  );
};

const LandingPage: React.FC = () => {
  const [dappName, setDappName] = useState('');

  return (
    <div className="snap-y snap-mandatory h-screen overflow-y-scroll">
      <header className="fixed top-0 left-0 right-0 bg-secondary py-4 shadow-lg z-50">
        <div className="container flex justify-between items-center">
          <div className="flex items-center">
            <img src={logo} alt="Logo" className="w-10 h-10 rounded-full mr-2" />
            <h1 className="text-2xl font-bold text-accent">create-kalp-dapp</h1>
          </div>
          <nav className="flex items-center">
            <a href="#home" className="text-text-light hover:text-accent mx-2">Home</a>
            <a href="#features" className="text-text-light hover:text-accent mx-2">Features</a>
            <a href="#getting-started" className="text-text-light hover:text-accent mx-2">Get Started</a>
            <a href="#showcase" className="text-text-light hover:text-accent mx-2">Showcase</a>
          </nav>
        </div>
      </header>

      <main>
        <Section id="home">
          <div className="text-center">
            <motion.h2 
              className="text-5xl font-extrabold mb-4 gradient-text"
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Build dApps on Kalp DLT
            </motion.h2>
            <motion.p 
              className="text-xl mb-8 text-text-light"
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              A full-stack starter kit featuring Next.js & Go for building dApps on the Kalp DLT chain.
            </motion.p>
            <motion.div 
              className="flex justify-center space-x-4"
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <a href="#getting-started" className="btn btn-primary glow-on-hover">
                  <FaRocket className="inline mr-2" /> Get Started
                </a>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <a href="https://github.com/Spydiecy/create-kalp-dapp" className="btn btn-secondary glow-on-hover">
                  <FaGithub className="inline mr-2" /> GitHub
                </a>
              </motion.div>
            </motion.div>
          </div>
        </Section>

        <Section id="features">
          <div className="container">
            <h2 className="text-4xl font-bold mb-12 text-center gradient-text">Features</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="card glow-on-hover"
                  whileHover={{ scale: 1.05 }}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  {feature.icon}
                  <h3 className="text-2xl font-semibold mb-2 text-accent">{feature.title}</h3>
                  <p className="text-text-light">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </Section>

        <Section id="getting-started">
          <div className="container">
            <h2 className="text-4xl font-bold mb-8 text-center gradient-text">Getting Started</h2>
            <motion.div
              className="card glow-on-hover max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-center">
                <p className="mb-4 text-text-light">Enter your dApp name:</p>
                <input
                  type="text"
                  value={dappName}
                  onChange={(e) => setDappName(e.target.value)}
                  placeholder="my-awesome-dapp"
                  className="input mb-4 w-full"
                />
                <pre className="code-block">
                  <code>{`npx create-kalp-dapp ${dappName || '<dapp-name>'}`}</code>
                </pre>
              </div>
            </motion.div>
            <p className="mt-4 text-center text-text-light">It's that simple! Your Kalp DLT dApp will be up and running in no time.</p>
          </div>
        </Section>

        <Section id="showcase">
          <div className="container">
            <h2 className="text-4xl font-bold mb-12 text-center gradient-text">Showcase</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div
                className="card glow-on-hover"
                whileHover={{ scale: 1.05 }}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h3 className="text-2xl font-semibold mb-2 text-accent">Greeting dApp</h3>
                <p className="mb-4 text-text-light">A simple dApp demonstrating smart contract interaction on Kalp DLT</p>
                <div className="h-60 bg-primary rounded-lg flex items-center justify-center">
                  <img src={demo} alt=""/>
                </div>
              </motion.div>
              <motion.div
                className="card glow-on-hover"
                whileHover={{ scale: 1.05 }}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h3 className="text-2xl font-semibold mb-2 text-accent">More Designs</h3>
                <p className="mb-4 text-text-light">More templates coming soon for you to build easily on the Kalp DLT</p>
                <div className="h-48 bg-primary rounded-lg flex items-center justify-center">
                  <span className="text-2xl text-accent">Coming soon...</span>
                </div>
              </motion.div>
            </div>
          </div>
        </Section>
      </main>

      <footer className="bg-secondary py-8 text-center">
        <div className="container">
          <p className="text-text-light">&copy; 2024 create-kalp-dapp. All rights reserved.</p>
          <div className="mt-4 flex justify-center space-x-4">
            <a href="https://github.com/Spydiecy/create-kalp-dapp" className="text-2xl text-text-light hover:text-accent">
              <FaGithub />
            </a>
            <a href="https://twitter.com/Tanishqistaken" className="text-2xl text-text-light hover:text-accent">
              <FaTwitter />
            </a>
            <a href="https://www.npmjs.com/package/create-kalp-dapp?activeTab=readme" className="text-2xl text-text-light hover:text-accent">
              <FaNpm />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
